import { useCallback, useRef, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'

export interface PageTab {
  id: string
  label: string
  /** A small count or marker shown beside the label. */
  badge?: ReactNode
}

/** A row of tabs in the same style as the course page's. Scrolls sideways rather than wrapping on a narrow phone. */
export function PageTabs({ tabs, active, onChange, label }: {
  tabs: PageTab[]
  active: string
  onChange: (id: string) => void
  label: string
}) {
  return (
    <div role="tablist" aria-label={label} className="flex gap-1 border-b border-gray-200 overflow-x-auto overflow-y-hidden">
      {tabs.map(t => (
        <button
          key={t.id}
          type="button"
          role="tab"
          id={`tab-${t.id}`}
          aria-selected={active === t.id}
          aria-controls={`panel-${t.id}`}
          onClick={() => onChange(t.id)}
          className={`shrink-0 inline-flex items-center px-2 sm:px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer border-b-2 -mb-px ${
            active === t.id
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t.label}
          {t.badge !== undefined && t.badge !== null && <span className="ml-1.5">{t.badge}</span>}
        </button>
      ))}
    </div>
  )
}

/** Renders its children only while its tab is the active one, so a hidden tab does no work (no map, no queries). */
export function TabPanel({ id, active, children }: { id: string; active: string; children: ReactNode }) {
  if (active !== id) return null
  return <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} className="space-y-6">{children}</div>
}

/** A little count pill for a tab label. */
export function TabCount({ children }: { children: ReactNode }) {
  return <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-600 tabular-nums">{children}</span>
}

/** A pulsing dot for a tab whose content is running right now. */
export function TabLiveDot({ label = 'Running' }: { label?: string }) {
  return <span role="img" aria-label={label} title={label} className="inline-block h-2 w-2 rounded-full bg-green-500 animate-pulse" />
}

/**
 * The active tab, kept in the address (`?tab=`) so a refresh or the Back button keeps your place.
 *
 * `defaultTab` is the tab to open on when the address doesn't name one. It is decided once, when the page first has
 * what it needs to decide (pass null until then), so something changing later, such as a live session starting,
 * doesn't pull you off the tab you're on.
 */
export function usePageTab(validIds: string[], defaultTab: string | null): [string, (id: string) => void] {
  const [params, setParams] = useSearchParams()
  const decided = useRef<string | null>(null)
  if (decided.current === null && defaultTab !== null) decided.current = defaultTab

  const requested = params.get('tab')
  const active =
    requested && validIds.includes(requested) ? requested
    : decided.current && validIds.includes(decided.current) ? decided.current
    : validIds[0]

  const setTab = useCallback((id: string) => {
    setParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('tab', id)
      return next
    }, { replace: true })
  }, [setParams])

  return [active, setTab]
}
